import { formatDistanceToNow } from "date-fns";
import { Key, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SecureTokenDisplay } from "@/components/ui/secure-token-display";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useTenant } from "@/contexts/TenantContext";
import { apiClient } from "@/lib/api";

interface Token {
	id: string;
	name: string;
	description?: string;
	scopes: string[];
	ecosystem?: string;
	expiresAt?: string;
	lastUsedAt?: string;
	createdAt: string;
	prefix: string;
}

export function TokensPage() {
	const { tenant } = useTenant();
	const [tokens, setTokens] = useState<Token[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [newTokenSecret, setNewTokenSecret] = useState<string | null>(null);
	const [tokenToDelete, setTokenToDelete] = useState<string | null>(null);

	// Form state
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		scopes: [] as string[],
		ecosystem: "all" as string,
		expiresAt: "",
	});

	const loadTokens = async () => {
		if (!tenant) return;

		try {
			setIsLoading(true);
			const response = (await apiClient.listTokens(tenant.id)) as any;
			setTokens(response.tokens || []);
		} catch (error) {
			console.error("Failed to load tokens:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (tenant) {
			loadTokens();
		}
	}, [tenant, loadTokens]);

	const handleCreateToken = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!tenant) return;

		try {
			const response = await apiClient.createToken(tenant.id, {
				name: formData.name,
				description: formData.description || undefined,
				scopes: formData.scopes,
				ecosystem:
					formData.ecosystem === "all" ? undefined : formData.ecosystem,
				expiresAt: formData.expiresAt || undefined,
			});

			setNewTokenSecret((response as any).secretToken);
			setIsCreateDialogOpen(false); // Close the dialog on success
			loadTokens(); // Refetch tokens to ensure data consistency

			// Reset form
			setFormData({
				name: "",
				description: "",
				scopes: [],
				ecosystem: "all",
				expiresAt: "",
			});
		} catch (error) {
			console.error("Failed to create token:", error);
			alert("Failed to create token. Please try again.");
		}
	};

	const handleDeleteToken = async (tokenId: string) => {
		if (!tenant) return;

		try {
			await apiClient.revokeToken(tenant.id, tokenId);
			setTokens(tokens.filter((t) => t.id !== tokenId));
			setTokenToDelete(null);
		} catch (error) {
			console.error("Failed to delete token:", error);
			alert("Failed to delete token. Please try again.");
		}
	};

	const toggleScope = (scope: string) => {
		setFormData({
			...formData,
			scopes: formData.scopes.includes(scope)
				? formData.scopes.filter((s) => s !== scope)
				: [...formData.scopes, scope],
		});
	};

	const availableScopes = [
		{
			value: "packages:read",
			label: "Read Packages",
			description: "Download and view packages",
		},
		{
			value: "packages:write",
			label: "Write Packages",
			description: "Publish and update packages",
		},
		{
			value: "packages:delete",
			label: "Delete Packages",
			description: "Remove packages",
		},
		{
			value: "admin:read",
			label: "Read Admin",
			description: "View tenant settings",
		},
		{
			value: "admin:write",
			label: "Write Admin",
			description: "Modify tenant settings",
		},
	];

	const ecosystems = [
		{ value: "all", label: "All Ecosystems" },
		{ value: "npm", label: "npm" },
		{ value: "pypi", label: "PyPI" },
		{ value: "cargo", label: "Cargo" },
		{ value: "docker", label: "Docker" },
		{ value: "maven", label: "Maven" },
	];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">API Tokens</h1>
					<p className="text-gray-500 mt-1">
						Manage API tokens for programmatic access to your registry
					</p>
				</div>
				<Button onClick={() => setIsCreateDialogOpen(true)}>
					<Plus className="w-4 h-4 mr-2" />
					Create Token
				</Button>
			</div>

			{/* Tokens table */}
			<Card>
				<CardHeader>
					<CardTitle>Active Tokens</CardTitle>
					<CardDescription>
						API tokens allow external applications to access your registry
					</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						</div>
					) : tokens.length === 0 ? (
						<div className="text-center py-12">
							<Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								No tokens yet
							</h3>
							<p className="text-gray-500 mb-4">
								Create your first API token to start publishing packages
							</p>
							<Button onClick={() => setIsCreateDialogOpen(true)}>
								<Plus className="w-4 h-4 mr-2" />
								Create Token
							</Button>
						</div>
					) : (
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Name</TableHead>
										<TableHead>Scopes</TableHead>
										<TableHead>Ecosystem</TableHead>
										<TableHead>Last Used</TableHead>
										<TableHead>Created</TableHead>
										<TableHead className="text-right">Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{tokens.map((token) => (
										<TableRow key={token.id}>
											<TableCell>
												<div>
													<div className="font-medium">{token.name}</div>
													{token.description && (
														<div className="text-sm text-gray-500">
															{token.description}
														</div>
													)}
													<div className="text-xs text-gray-400 mt-1 font-mono">
														{token.prefix}•••
													</div>
												</div>
											</TableCell>
											<TableCell>
												<div className="flex flex-wrap gap-1">
													{token.scopes.map((scope) => (
														<Badge key={scope} variant="secondary">
															{scope}
														</Badge>
													))}
												</div>
											</TableCell>
											<TableCell>
												<Badge variant="outline">
													{token.ecosystem || "all"}
												</Badge>
											</TableCell>
											<TableCell>
												{token.lastUsedAt
													? formatDistanceToNow(new Date(token.lastUsedAt), {
															addSuffix: true,
														})
													: "Never"}
											</TableCell>
											<TableCell>
												{formatDistanceToNow(new Date(token.createdAt), {
													addSuffix: true,
												})}
											</TableCell>
											<TableCell className="text-right">
												<Button
													variant="destructive"
													size="sm"
													onClick={() => setTokenToDelete(token.id)}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Create token dialog */}
			<Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
				<DialogContent className="max-w-2xl">
					<form onSubmit={handleCreateToken}>
						<DialogHeader>
							<DialogTitle>Create API Token</DialogTitle>
							<DialogDescription>
								Generate a new token for programmatic access to your registry
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="name">Token Name *</Label>
								<Input
									id="name"
									placeholder="e.g., CI/CD Pipeline Token"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
									required
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="description">Description</Label>
								<Input
									id="description"
									placeholder="What will this token be used for?"
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
								/>
							</div>

							<div className="space-y-2">
								<Label>Scopes *</Label>
								<div className="space-y-2">
									{availableScopes.map((scope) => (
										<label
											key={scope.value}
											className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
										>
											<input
												type="checkbox"
												checked={formData.scopes.includes(scope.value)}
												onChange={() => toggleScope(scope.value)}
												className="mt-1"
											/>
											<div className="flex-1">
												<div className="font-medium text-sm">{scope.label}</div>
												<div className="text-xs text-gray-500">
													{scope.description}
												</div>
											</div>
										</label>
									))}
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="ecosystem">Ecosystem</Label>
								<Select
									value={formData.ecosystem}
									onValueChange={(value) =>
										setFormData({ ...formData, ecosystem: value })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{ecosystems.map((eco) => (
											<SelectItem key={eco.value} value={eco.value}>
												{eco.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="expiresAt">Expiration (optional)</Label>
								<Input
									id="expiresAt"
									type="datetime-local"
									value={formData.expiresAt}
									onChange={(e) =>
										setFormData({ ...formData, expiresAt: e.target.value })
									}
								/>
							</div>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setIsCreateDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={!formData.name || formData.scopes.length === 0}
							>
								Create Token
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* New token secret dialog */}
			<Dialog
				open={!!newTokenSecret}
				onOpenChange={() => setNewTokenSecret(null)}
			>
				<DialogContent className="max-w-2xl">
					<SecureTokenDisplay
						token={newTokenSecret || ""}
						onClose={() => setNewTokenSecret(null)}
						title="Token Created Successfully"
						description="Make sure to copy your token now. You will not be able to see it again!"
					/>
				</DialogContent>
			</Dialog>

			{/* Delete confirmation dialog */}
			<Dialog
				open={!!tokenToDelete}
				onOpenChange={() => setTokenToDelete(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Token</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this token? This action cannot be
							undone.
						</DialogDescription>
					</DialogHeader>

					<DialogFooter>
						<Button variant="outline" onClick={() => setTokenToDelete(null)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={() => tokenToDelete && handleDeleteToken(tokenToDelete)}
						>
							Delete Token
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
